import { Spacer } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AccessDenied } from "src/components/Feedback/AccessDenied";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as FormTemplate from "src/services/formTemplate";
import * as Url from "src/services/url";
import { ENROLLMENT_PERIOD_TAB_HASH } from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { EditFormTemplateBackButton } from "../../components/BackButton";
import { useFormTemplateContext } from "../../formTemplateContext";
import {
  GET_FORM_TEMPLATE_CONTENT_BY_ID,
  GET_GRADES_CONFIG_BY_ORGANIZATION,
  GET_QUESTION_TYPES_BY_ORGANIZATION,
} from "../../graphql/queries";
import { useFormBuilderV1Feature } from "../../hooks/useFormBuilderV1Feature";
import { Content } from "./Content";
import { EditFormTemplateProvider } from "./context";
import { FormButtons } from "./FormButtons";

export const ContentTab: React.FC<{}> = () => {
  const { formTemplate, organization } = useFormTemplateContext();
  const navigate = useNavigate();
  const { enrollmentPeriodId = "" } = useParams();

  const { isFormBuilderReadOnly, isFormBuilderFullEdit } =
    useFormBuilderV1Feature();

  const { remoteData: formTemplateData } = useRemoteDataQuery<
    GQL.GetFormTemplateContentById,
    GQL.GetFormTemplateContentByIdVariables
  >(GET_FORM_TEMPLATE_CONTENT_BY_ID, {
    variables: {
      form_template_id: formTemplate.id,
    },
    fetchPolicy: "network-only",
    skip: !formTemplate.id,
  });

  const { remoteData: gradesConfigData } = useRemoteDataQuery<
    GQL.GetGradesConfigByOrganization,
    GQL.GetGradesConfigByOrganizationVariables
  >(GET_GRADES_CONFIG_BY_ORGANIZATION, {
    variables: {
      organizationId: organization.id,
    },
    fetchPolicy: "network-only",
    skip: !organization.id,
  });

  const { remoteData: questionTypesRemoteData } = useRemoteDataQuery<
    GQL.GetQuestionTypesByOrganization,
    GQL.GetQuestionTypesByOrganizationVariables
  >(GET_QUESTION_TYPES_BY_ORGANIZATION, {
    variables: {
      organizationId: organization.id,
    },
    fetchPolicy: "network-only",
    skip: !organization.id,
  });

  const formTemplateContentRD = useMemo(
    () =>
      formTemplateData.map((data) =>
        data.form_template_by_pk
          ? FormTemplate.fromGQL(data.form_template_by_pk, "lenient")
          : undefined
      ),

    [formTemplateData]
  );

  if (!isFormBuilderReadOnly && !isFormBuilderFullEdit) {
    const accessDeniedRedirectRoute = Url.OrgAdmin.Enrollments.edit(
      organization,
      enrollmentPeriodId,
      ENROLLMENT_PERIOD_TAB_HASH.FORMS
    );
    setTimeout(() => {
      navigate(accessDeniedRedirectRoute);
    }, 2000);
    return (
      <AccessDenied
        message="You don't have access to this page, redirecting..."
        isRedirect={true}
      />
    );
  }

  return (
    <RemoteDataView
      remoteData={formTemplateContentRD}
      error={(error) => <GenericError error={error} />}
      loading={<Loading />}
    >
      {(formTemplate) => {
        if (!formTemplate) {
          return <NotFound heading="" text="Form not found" />;
        }

        return (
          <EditFormTemplateProvider
            formTemplate={formTemplate}
            customQuestionTypes={
              questionTypesRemoteData.hasData()
                ? questionTypesRemoteData.data.custom_question_type
                : []
            }
          >
            <Content
              organizationId={organization.id}
              gradesConfig={
                gradesConfigData.hasData()
                  ? gradesConfigData.data.grade_config
                  : []
              }
            />
            <Spacer />
            <AdminFormButtons insideTabPanel>
              <EditFormTemplateBackButton />
              <Spacer />
              <FormButtons formTemplate={formTemplate} />
            </AdminFormButtons>
          </EditFormTemplateProvider>
        );
      }}
    </RemoteDataView>
  );
};
